/**
 * ERROR ANALYTICS PAGE
 *
 * Track and analyze picking errors for training and improvement.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  Select,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import LogErrorModal from '../components/LogErrorModal';

interface PickingError {
  id: number;
  order_number: string;
  product_sku: string;
  error_type: string;
  expected_sku: string | null;
  actual_sku: string | null;
  expected_quantity: number | null;
  actual_quantity: number | null;
  picker_id: string | null;
  picker_name: string | null;
  detected_by: string;
  detected_at: string;
  notes: string | null;
  created_at: string;
}

interface PickerStats {
  picker_id: string;
  picker_name: string;
  total_picks: number;
  total_errors: number;
  error_rate: number;
}

interface ErrorAnalytics {
  total_errors: number;
  errors_this_week: number;
  errors_this_month: number;
  error_rate: number;
  by_type: Array<{ error_type: string; count: number; percentage: number }>;
  by_picker: PickerStats[];
  recent_errors: PickingError[];
}

export default function ErrorAnalytics() {
  const { fetchApi, loading, error } = useApi();
  const [analytics, setAnalytics] = useState<ErrorAnalytics | null>(null);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [period, setPeriod] = useState('month');

  const loadAnalytics = useCallback(async () => {
    const data = await fetchApi<ErrorAnalytics>(`/reports/error-analytics?period=${period}`);
    if (data) {
      setAnalytics(data);
    }
  }, [fetchApi, period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const getErrorTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      wrong_item: 'Wrong Item',
      wrong_quantity: 'Wrong Quantity',
      missed_item: 'Missed Item',
      wrong_location: 'Wrong Location',
      damaged: 'Damaged During Pick',
    };
    return labels[type] || type;
  };

  const getErrorTypeBadge = (type: string) => {
    const tones: Record<string, 'critical' | 'warning' | undefined> = {
      wrong_item: 'critical',
      wrong_quantity: 'warning',
      missed_item: 'critical',
      wrong_location: 'warning',
      damaged: 'critical',
    };
    return <Badge tone={tones[type]}>{getErrorTypeLabel(type)}</Badge>;
  };

  const getDetectedByLabel = (by: string) => {
    const labels: Record<string, string> = {
      packer: 'Packer',
      customer: 'Customer',
      qa: 'QA Check',
      driver: 'Driver',
    };
    return labels[by] || by;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !analytics) {
    return (
      <Page title="Error Analytics">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading analytics...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Error Analytics"
      subtitle="Track picking errors and identify training opportunities"
      primaryAction={{
        content: 'Log Error',
        onAction: () => setLogModalOpen(true),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Period Filter */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ width: '200px' }}>
                <Select
                  label="Period"
                  options={[
                    { label: 'This Week', value: 'week' },
                    { label: 'This Month', value: 'month' },
                    { label: 'Last 3 Months', value: '3months' },
                  ]}
                  value={period}
                  onChange={setPeriod}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {analytics && (
          <>
            {/* Summary Cards */}
            <Layout.Section>
              <InlineStack gap="400" wrap>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Total Errors</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold" tone="critical">
                      {analytics.total_errors}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">This Week</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold">
                      {analytics.errors_this_week}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Error Rate</Text>
                    <Text
                      as="p"
                      variant="headingXl"
                      fontWeight="bold"
                      tone={analytics.error_rate > 2 ? 'critical' : 'success'}
                    >
                      {analytics.error_rate.toFixed(2)}%
                    </Text>
                  </BlockStack>
                </Card>
              </InlineStack>
            </Layout.Section>

            {/* Error Breakdown & Picker Performance */}
            <Layout.Section variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Errors by Type</Text>

                  {analytics.by_type.length > 0 ? (
                    <BlockStack gap="300">
                      {analytics.by_type.map((item) => (
                        <InlineStack key={item.error_type} align="space-between">
                          {getErrorTypeBadge(item.error_type)}
                          <InlineStack gap="200">
                            <Text as="span" fontWeight="semibold">{item.count}</Text>
                            <Text as="span" tone="subdued">({item.percentage.toFixed(1)}%)</Text>
                          </InlineStack>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">No errors recorded.</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Picker Performance</Text>

                  {analytics.by_picker.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                      headings={['Picker', 'Picks', 'Errors', 'Rate']}
                      rows={analytics.by_picker.map((picker) => [
                        picker.picker_name || picker.picker_id || 'Unknown',
                        picker.total_picks,
                        picker.total_errors,
                        <Badge
                          key={`rate-${picker.picker_id}`}
                          tone={picker.error_rate > 2 ? 'critical' : picker.error_rate > 1 ? 'warning' : 'success'}
                        >
                          {`${picker.error_rate.toFixed(2)}%`}
                        </Badge>,
                      ])}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No picker data available.</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Recent Errors */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Recent Errors</Text>

                  {analytics.recent_errors.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                      headings={['Date', 'Order', 'SKU', 'Type', 'Picker', 'Detected By']}
                      rows={analytics.recent_errors.map((err) => [
                        formatDate(err.created_at),
                        err.order_number || '-',
                        err.product_sku || '-',
                        getErrorTypeBadge(err.error_type),
                        err.picker_name || '-',
                        getDetectedByLabel(err.detected_by),
                      ])}
                    />
                  ) : (
                    <EmptyState
                      heading="No errors recorded"
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                    >
                      <p>
                        No picking errors have been logged. Use the "Log Error" button
                        to record issues when they're discovered.
                      </p>
                    </EmptyState>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>

      <LogErrorModal
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        onSaved={loadAnalytics}
      />
    </Page>
  );
}
